import { useEffect, useRef, useState } from 'react';
import type { Broadcast, MetricSample, PublisherCredentials } from '@ivs-test/contracts';
import { api } from './api.js';
import { createMeasuredStream, estimateClockOffset, type MeasuredStream } from './media.js';

interface ActiveBroadcast {
  credentials: PublisherCredentials;
  source: MediaStream;
  measured?: MeasuredStream;
  stopSdk(): void;
  connectionState(): string;
  telemetry(): Promise<Partial<MetricSample>>;
  setAudioMuted(muted: boolean): void;
  setVideoMuted(muted: boolean): void;
}

interface QualityStat {
  bytesSent?: number;
  framesSent?: number;
  networkQuality?: number;
  packetsLost?: number;
  packetsSent?: number;
  qualityLimitationReason?: string;
}

function networkType(): string | undefined {
  return (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;
}

export function IvsBroadcaster({ broadcast }: { broadcast: Broadcast }) {
  const previewRef = useRef<HTMLVideoElement>(null);
  const activeRef = useRef<ActiveBroadcast | undefined>(undefined);
  const [status, setStatus] = useState('停止中');
  const [error, setError] = useState('');
  const [measured, setMeasured] = useState(false);
  const [runId, setRunId] = useState('');
  const [clientId] = useState(() => `web-${crypto.randomUUID()}`);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDeviceId, setVideoDeviceId] = useState('');
  const [audioDeviceId, setAudioDeviceId] = useState('');
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);

  const refreshDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    setVideoDevices(devices.filter((device) => device.kind === 'videoinput'));
    setAudioDevices(devices.filter((device) => device.kind === 'audioinput'));
  };

  useEffect(() => {
    void refreshDevices();
    const refresh = () => void refreshDevices();
    navigator.mediaDevices.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices.removeEventListener?.('devicechange', refresh);
  }, []);

  const postSample = (sample: Partial<MetricSample>) => {
    if (!runId) return;
    void api.postTelemetry(runId, [{
      timestamp: new Date().toISOString(),
      clientId,
      role: 'PUBLISHER',
      userAgent: navigator.userAgent,
      networkType: networkType(),
      ...sample,
    }]);
  };

  const stop = async () => {
    const active = activeRef.current;
    activeRef.current = undefined;
    if (!active) return;
    try {
      active.stopSdk();
      active.measured?.stop();
      active.source.getTracks().forEach((track) => track.stop());
      await api.closePublisherSession(active.credentials.sessionId, broadcast.id);
    } finally {
      if (previewRef.current) previewRef.current.srcObject = null;
      setAudioMuted(false);
      setVideoMuted(false);
      setStatus('停止中');
    }
  };

  useEffect(() => () => void stop(), [broadcast.id]);

  useEffect(() => {
    if (!activeRef.current) return;
    const heartbeat = window.setInterval(() => {
      const active = activeRef.current;
      if (active) {
        void api.heartbeatPublisherSession(active.credentials.sessionId, broadcast.id).catch((caught: unknown) => {
          postSample({ errorCode: caught instanceof Error ? caught.message : 'HEARTBEAT_FAILED' });
        });
      }
    }, 30_000);
    const telemetry = window.setInterval(() => {
      const active = activeRef.current;
      if (!active || !runId) return;
      void active.telemetry()
        .then((quality) => postSample({ connectionState: active.connectionState(), ...quality }))
        .catch((caught: unknown) => postSample({ errorCode: caught instanceof Error ? caught.message : 'QUALITY_STATS_FAILED' }));
    }, 10_000);
    return () => {
      window.clearInterval(heartbeat);
      window.clearInterval(telemetry);
    };
  }, [broadcast.id, clientId, runId, status]);

  const start = async (cameraOverride?: string) => {
    setError('');
    setStatus('準備中');
    let credentials: PublisherCredentials | undefined;
    let source: MediaStream | undefined;
    try {
      credentials = await api.createPublisherSession(broadcast.id, clientId);
      const profile = broadcast.encoderProfile;
      const selectedVideo = cameraOverride ?? videoDeviceId;
      source = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: profile.width },
          height: { ideal: profile.height },
          frameRate: { ideal: profile.fps, max: profile.fps },
          ...(selectedVideo ? { deviceId: { exact: selectedVideo } } : { facingMode: 'user' }),
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          ...(audioDeviceId ? { deviceId: { exact: audioDeviceId } } : {}),
        },
      });
      await refreshDevices();
      const clockOffset = measured ? await estimateClockOffset() : 0;
      const measuredStream = measured
        ? await createMeasuredStream(source, profile.width, profile.height, profile.fps, clockOffset)
        : undefined;
      const outgoing = measuredStream?.stream ?? source;
      if (previewRef.current) {
        previewRef.current.srcObject = outgoing;
        await previewRef.current.play();
      }

      if (credentials.kind === 'LOW_LATENCY') {
        const sdk = await import('amazon-ivs-web-broadcast');
        const client = sdk.default.create({
          streamConfig: {
            maxResolution: { width: profile.width, height: profile.height },
            maxFramerate: profile.fps,
            maxBitrate: profile.videoBitrateKbps,
          },
          ingestEndpoint: credentials.ingestEndpoint,
          networkReconnectConfig: { reconnect: true },
        });
        const videoStream = new MediaStream(outgoing.getVideoTracks());
        const audioStream = new MediaStream(outgoing.getAudioTracks());
        await client.addVideoInputDevice(videoStream, 'camera', { index: 0, x: 0, y: 0, width: profile.width, height: profile.height });
        if (audioStream.getAudioTracks().length > 0) await client.addAudioInputDevice(audioStream, 'microphone');
        let connectionState = String(client.getConnectionState());
        let reconnectCount = 0;
        client.on(sdk.BroadcastClientEvents.CONNECTION_STATE_CHANGE, (...eventArguments: unknown[]) => {
          const nextState = String(eventArguments[0] ?? 'UNKNOWN');
          if (connectionState === 'CONNECTED' && nextState !== 'CONNECTED') reconnectCount += 1;
          connectionState = nextState;
          postSample({ connectionState: nextState, reconnectCount: nextState === 'CONNECTED' ? reconnectCount : 0 });
        });
        client.on(sdk.BroadcastClientEvents.ERROR, (...eventArguments: unknown[]) => {
          const sdkError = (eventArguments[0] ?? {}) as { code?: number; message?: string };
          const message = sdkError.message ?? 'IVS Web Broadcast SDK error';
          setError(message);
          postSample({ errorCode: String(sdkError.code ?? message), connectionState });
        });
        await client.startBroadcast(credentials.streamKey, credentials.ingestEndpoint);
        activeRef.current = {
          credentials,
          source,
          ...(measuredStream ? { measured: measuredStream } : {}),
          stopSdk: () => client.stopBroadcast(),
          connectionState: () => connectionState,
          telemetry: async () => ({ sessionId: client.getSessionId() }),
          setAudioMuted: (muted) => (muted ? client.disableAudio() : client.enableAudio()),
          setVideoMuted: (muted) => (muted ? client.disableVideo() : client.enableVideo()),
        };
      } else {
        const sdk = await import('amazon-ivs-web-broadcast');
        const streams = outgoing.getTracks().map((track) => new sdk.LocalStageStream(
          track,
          track.kind === 'video'
            ? { maxVideoBitrateKbps: profile.videoBitrateKbps, maxFramerate: profile.fps }
            : { maxAudioBitrateKbps: profile.audioBitrateKbps },
        ));
        const strategy = {
          stageStreamsToPublish: () => streams,
          shouldPublishParticipant: () => true,
          shouldSubscribeToParticipant: () => sdk.SubscribeType.AUDIO_VIDEO,
        };
        const stage = new sdk.Stage(credentials.participantToken, strategy);
        let connectionState = 'CONNECTING';
        let reconnectCount = 0;
        let previous = { at: Date.now(), bytes: 0, frames: 0 };
        stage.on(sdk.StageEvents.STAGE_CONNECTION_STATE_CHANGED, (nextState) => {
          const next = String(nextState);
          if (connectionState === 'CONNECTED' && next !== 'CONNECTED') reconnectCount += 1;
          connectionState = next;
          postSample({ connectionState: next, reconnectCount: next === 'CONNECTED' ? reconnectCount : 0 });
        });
        stage.on(sdk.StageEvents.ERROR, (sdkError) => {
          setError(sdkError.message);
          postSample({ errorCode: String(sdkError.code), connectionState });
        });
        await stage.join();
        activeRef.current = {
          credentials,
          source,
          ...(measuredStream ? { measured: measuredStream } : {}),
          stopSdk: () => stage.leave(),
          connectionState: () => connectionState,
          telemetry: async () => {
            const groups = await Promise.all(streams.map((stream) => stream.requestQualityStats()));
            const stats = groups.flatMap((group) => (group ?? []) as QualityStat[]);
            const current = {
              at: Date.now(),
              bytes: stats.reduce((sum, stat) => sum + (stat.bytesSent ?? 0), 0),
              frames: stats.reduce((sum, stat) => sum + (stat.framesSent ?? 0), 0),
            };
            const elapsedSeconds = Math.max(0.001, (current.at - previous.at) / 1000);
            const packetsSent = stats.reduce((sum, stat) => sum + (stat.packetsSent ?? 0), 0);
            const packetsLost = stats.reduce((sum, stat) => sum + (stat.packetsLost ?? 0), 0);
            const result: Partial<MetricSample> = {
              bitrateKbps: Math.max(0, ((current.bytes - previous.bytes) * 8) / elapsedSeconds / 1000),
              framesPerSecond: Math.max(0, (current.frames - previous.frames) / elapsedSeconds),
              packetLossPercent: packetsSent + packetsLost > 0 ? (packetsLost / (packetsSent + packetsLost)) * 100 : 0,
              quality: stats.map((stat) => stat.qualityLimitationReason ?? String(stat.networkQuality ?? '')).filter(Boolean).join('/'),
            };
            previous = current;
            return result;
          },
          setAudioMuted: (muted) => streams.filter((stream) => stream.mediaStreamTrack.kind === 'audio').forEach((stream) => stream.setMuted(muted)),
          setVideoMuted: (muted) => streams.filter((stream) => stream.mediaStreamTrack.kind === 'video').forEach((stream) => stream.setMuted(muted)),
        };
      }
      setStatus('配信中');
    } catch (caught) {
      source?.getTracks().forEach((track) => track.stop());
      setError(caught instanceof Error ? caught.message : '配信の開始に失敗しました');
      setStatus('エラー');
      if (credentials) await api.closePublisherSession(credentials.sessionId, broadcast.id).catch(() => undefined);
    }
  };

  const toggleAudio = () => {
    const next = !audioMuted;
    activeRef.current?.setAudioMuted(next);
    setAudioMuted(next);
  };

  const toggleVideo = () => {
    const next = !videoMuted;
    activeRef.current?.setVideoMuted(next);
    setVideoMuted(next);
  };

  const switchCamera = async () => {
    if (videoDevices.length < 2) return;
    const currentIndex = videoDevices.findIndex((device) => device.deviceId === videoDeviceId);
    const next = videoDevices[(currentIndex + 1 + videoDevices.length) % videoDevices.length];
    if (!next) return;
    const wasActive = Boolean(activeRef.current);
    if (wasActive) await stop();
    setVideoDeviceId(next.deviceId);
    if (wasActive) await start(next.deviceId);
  };

  return (
    <section className="panel">
      <div className="section-heading">
        <div><span className="eyebrow">BROADCAST</span><h2>ブラウザ配信</h2></div>
        <span className={`status ${status === '配信中' ? 'live' : ''}`}>{status}</span>
      </div>
      <video ref={previewRef} className="video" muted playsInline />
      <div className="form-grid compact">
        <label>カメラ<select value={videoDeviceId} onChange={(event) => setVideoDeviceId(event.target.value)} disabled={Boolean(activeRef.current)}><option value="">自動（前面）</option>{videoDevices.map((device, index) => <option key={device.deviceId} value={device.deviceId}>{device.label || `カメラ ${index + 1}`}</option>)}</select></label>
        <label>マイク<select value={audioDeviceId} onChange={(event) => setAudioDeviceId(event.target.value)} disabled={Boolean(activeRef.current)}><option value="">自動</option>{audioDevices.map((device, index) => <option key={device.deviceId} value={device.deviceId}>{device.label || `マイク ${index + 1}`}</option>)}</select></label>
        <label>固定エンコーダ設定<input readOnly value={`${broadcast.encoderProfile.orientation} / ${broadcast.encoderProfile.width}×${broadcast.encoderProfile.height} / ${broadcast.encoderProfile.fps}fps`} /></label>
        <label>Bitrate<input readOnly value={`映像 ${broadcast.encoderProfile.videoBitrateKbps} / 音声 ${broadcast.encoderProfile.audioBitrateKbps} Kbps`} /></label>
        <label>計測Run ID（任意）<input value={runId} onChange={(event) => setRunId(event.target.value)} placeholder="UUID" /></label>
        <label className="check-row"><input type="checkbox" checked={measured} onChange={(event) => setMeasured(event.target.checked)} disabled={Boolean(activeRef.current)} />映像に時刻・QRを合成</label>
      </div>
      {broadcast.kind === 'LOW_LATENCY' && <p className="notice">Web Broadcast SDKでは音声bitrateを直接指定できないため、設定値と実測値を分けて記録します。</p>}
      <div className="actions">
        <button className="primary" onClick={() => void start()} disabled={status === '配信中' || status === '準備中'}>配信開始</button>
        <button onClick={() => void stop()} disabled={!activeRef.current}>停止</button>
        <button onClick={toggleAudio} disabled={!activeRef.current}>{audioMuted ? 'マイクON' : 'マイクOFF'}</button>
        <button onClick={toggleVideo} disabled={!activeRef.current}>{videoMuted ? '映像ON' : '映像OFF'}</button>
        <button onClick={() => void switchCamera()} disabled={videoDevices.length < 2}>カメラ切替</button>
      </div>
      {error && <p className="error">{error}</p>}
    </section>
  );
}
